import React, { useEffect, useState } from "react";
import Layout from "../layout/Layout";
import {
  Box,
  Card,
  Grid,
  MenuItem,
  Select,
  Typography,
  Avatar,
  Button,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Divider,
  Badge,
  CircularProgress,
  Fab,
  Zoom,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { useNavigate } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import {
  config,
  baseURL,
  dashboardAPI,
  manageCompaniesAPI,
  hrPayrollMetricsAPI,
  leaveAPI,
  hrMasterListAPI,
  advancePaymentAPI,
  employeeAttandenceAPI,
} from "../config";
import moment from "moment";
import axios from "axios";

// Icons
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import BlockIcon from "@mui/icons-material/Block";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AlarmIcon from "@mui/icons-material/Alarm";
import PaymentsIcon from "@mui/icons-material/Payments";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PersonIcon from "@mui/icons-material/Person";

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const YEARS = Array.from({ length: 7 }, (_, i) => moment().year() - 3 + i);

function Dashboard() {
  const navigate = useNavigate();

  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const greetingEmployeeName = userData.username || "";

  // Dynamic monthly attendance chart trends
  const [trendPresent, setTrendPresent] = useState([]);
  const [trendAbsent, setTrendAbsent] = useState([]);
  const [trendLeave, setTrendLeave] = useState([]);

  // Filters State
  const [companyID, setCompanyID] = useState(0);
  const [selectedYear, setSelectedYear] = useState(moment().year());
  const [selectedMonth, setSelectedMonth] = useState(moment().month() + 1);

  // Data States
  const [companyList, setCompanyList] = useState([]);
  const [hrMasterList, setHrMasterList] = useState([]);
  const [allLeaves, setAllLeaves] = useState([]);
  const [allAdvances, setAllAdvances] = useState([]);
  const [attendanceRate, setAttendanceRate] = useState(0);
  const [attendanceRateDetails, setAttendanceRateDetails] = useState({
    logged_in_percentage: 0,
    not_logged_in_percentage: 0,
    leave_percentage: 0,
  });

  // Real Payroll Batch States Sourced from Payroll Master API
  const [payrollBatch, setPayrollBatch] = useState(null);
  const [payrollEmployees, setPayrollEmployees] = useState([]);
  const [lastUpdated, setLastUpdated] = useState("");

  const [hrPayrollMetrics, setHrPayrollMetrics] = useState({
    leaves: {
      thisMonth: { employees: 0, days: 0 },
      lastMonth: { employees: 0, days: 0 },
    },
    salary: {
      thisMonth: 0,
      lastMonth: 0,
    },
    advances: {
      thisMonth: { given: 0, deducted: 0 },
      lastMonth: { given: 0, deducted: 0 },
    },
    longDaysInactive: [],
    genderBreakdown: [],
  });

  const [companiesPayroll, setCompaniesPayroll] = useState({});
  const [absentEmployeesToday, setAbsentEmployeesToday] = useState([]);
  const [neverLoggedInEmployees, setNeverLoggedInEmployees] = useState([]);
  const [dailyAttendanceLogs, setDailyAttendanceLogs] = useState([]);

  const refreshData = () => {
    setLastUpdated(moment().format("hh:mm A"));
    fetchData();
  };

  const fetchPayrollBatch = (activeCompanyId) => {
    axios({
      ...config,
      method: "GET",
      url: `${baseURL}/payroll/list/${activeCompanyId}`,
    })
      .then((res) => {
        const batchList = res.data.result || [];
        const formattedMonth = moment(`${selectedYear}-${selectedMonth}`, "YYYY-M").format("YYYY-MM");
        const active = batchList.find((b) => b.period_month_year === formattedMonth);

        if (active) {
          setPayrollBatch(active);
          // Load batch employees to compute workflow calculation completeness
          axios({
            ...config,
            method: "GET",
            url: `${baseURL}/payroll/batch/${active.id}/employees`,
          }).then((empRes) => {
            setPayrollEmployees(empRes.data.result || []);
          });
        } else {
          setPayrollBatch(null);
          setPayrollEmployees([]);
        }
      })
      .catch((err) => console.log(err));
  };

  const fetchData = () => {
    // 1. Fetch Companies list
    axios
      .post(manageCompaniesAPI, { intType: 6 })
      .then((res) => {
        const companies = res.data.result || [];
        setCompanyList(companies);

        // Fetch payroll batch list using the first company if companyID is 0
        const activeId = companyID !== 0 ? companyID : (companies.length > 0 ? companies[0].id : 20);
        fetchPayrollBatch(activeId);

        // Fetch payroll lists for all companies to determine missing payroll runs
        const formattedMonth = moment(`${selectedYear}-${selectedMonth}`, "YYYY-M").format("YYYY-MM");
        const promises = companies.map((comp) => {
          return axios({
            ...config,
            method: "GET",
            url: `${baseURL}/payroll/list/${comp.id}`,
          })
            .then((pRes) => {
              const batchList = pRes.data.result || [];
              const batch = batchList.find((b) => b.period_month_year === formattedMonth);
              return { compId: comp.id, compName: comp.company_name, hasPayroll: !!batch, status: batch?.status };
            })
            .catch(() => {
              return { compId: comp.id, compName: comp.company_name, hasPayroll: false, status: null };
            });
        });

        Promise.all(promises).then((results) => {
          const statusMap = {};
          results.forEach((r) => {
            statusMap[r.compId] = r;
          });
          setCompaniesPayroll(statusMap);
        });
      })
      .catch((err) => console.log(err));

    // 2. Fetch all employees
    let hrReq = {
      pageIndex: 0,
      pageSize: 1000,
      filters: companyID !== 0 ? [{ id: "company_name", value: companyID }] : [],
      sorting: [],
    };
    const cnf1 = { ...config, method: "POST", url: hrMasterListAPI, data: JSON.stringify(hrReq) };
    axios(cnf1)
      .then((res) => {
        setHrMasterList(res.data.result || []);
      })
      .catch((err) => console.log(err));

    // 3. Remove hardcoded today's attendance fetch because we calculate it dynamically for the selected month/year

    // 4. Fetch leaves list for leave intelligence
    let leaveReq = {
      pageSize: 1000,
      pageIndex: 0,
      intType: 8,
      columnFilters: [],
      sorting: [],
    };
    const cnf3 = { ...config, method: "POST", url: leaveAPI, data: JSON.stringify(leaveReq) };
    axios(cnf3)
      .then((res) => {
        setAllLeaves(res.data.result || []);
      })
      .catch((err) => console.log(err));

    // 5. Fetch all advances for advances calculation
    let advanceReq = {
      intLimit: 1000,
      intOffset: 0,
      intType: 3,
      intPaymentStatus: 0, // Fetch all advances, not just pending ones
    };
    const cnf4 = { ...config, method: "POST", url: advancePaymentAPI, data: JSON.stringify(advanceReq) };
    axios(cnf4)
      .then((res) => {
        setAllAdvances(res.data.result || []);
      })
      .catch((err) => console.log(err));

    // 6. Fetch payroll & advance metrics
    let metricsReq = {
      companyID: companyID,
    };
    const cnf5 = { ...config, method: "POST", url: hrPayrollMetricsAPI, data: JSON.stringify(metricsReq) };
    axios(cnf5)
      .then((res) => {
        if (res.data.success) {
          setHrPayrollMetrics(res.data.result);
        }
      })
      .catch((err) => console.log(err));

    // 7. Fetch daily attendance to find absent employees today
    let dailyReq = {
      intLimit: 1000,
      intOffset: 0,
      txtStartTime: moment().format("YYYY-MM-DD"),
      txtEndTime: moment().format("YYYY-MM-DD"),
      intEmployeeID: 0,
      intCompanyId: companyID,
      intType: 16,
    };
    const cnfDaily = { ...config, method: "POST", url: employeeAttandenceAPI, data: JSON.stringify(dailyReq) };
    axios(cnfDaily)
      .then((res) => {
        const list = res.data.data || [];
        setDailyAttendanceLogs(list);
        // Filter active employees who have not clocked in (no start_time)
        const absents = list.filter((emp) => !emp.start_time);
        setAbsentEmployeesToday(absents);
      })
      .catch((err) => console.log(err));

    // 8. Fetch monthly attendance report to check who never logged in for this whole month and calculate attendance rate & trends
    let monthlyReq = {
      txtStartTime: moment(`${selectedYear}-${selectedMonth}-01`, "YYYY-M-D").format("YYYY-MM-DD"),
      intCompanyId: companyID,
      intEmployeeID: 0,
      intType: 7,
    };
    const cnfMonthly = { ...config, method: "POST", url: employeeAttandenceAPI, data: JSON.stringify(monthlyReq) };
    axios(cnfMonthly)
      .then((res) => {
        const list = res.data.result || [];
        const neverLoggedIn = list.filter((emp) => {
          let hasPresentDays = false;
          for (let key in emp) {
            if (!isNaN(key)) {
              if (emp[key] === 1 || emp[key] === 2) {
                hasPresentDays = true;
                break;
              }
            }
          }
          return !hasPresentDays && emp.employee_name;
        });
        setNeverLoggedInEmployees(neverLoggedIn);

        // Calculate dynamic attendance rate and daily trends from the monthly report
        if (list.length > 0) {
          const daysInMonthVal = moment(`${selectedYear}-${selectedMonth}`, "YYYY-M").daysInMonth();
          const categoriesDaysVal = Array.from({ length: daysInMonthVal }, (_, i) => i + 1);

          let totalPresentDays = 0;
          let totalAbsentDays = 0;
          let totalLeaveDays = 0;

          const tempTrendPresent = categoriesDaysVal.map((dayNum) => {
            const dayStr = dayNum.toString().padStart(2, '0');
            let count = 0;
            list.forEach((emp) => {
              const status = emp[dayStr];
              if (status === 1 || status === 2) count++;
            });
            return count;
          });

          const tempTrendAbsent = categoriesDaysVal.map((dayNum) => {
            const dayStr = dayNum.toString().padStart(2, '0');
            let count = 0;
            list.forEach((emp) => {
              const status = emp[dayStr];
              if (status === 3 || status === null || status === undefined) count++;
            });
            return count;
          });

          const tempTrendLeave = categoriesDaysVal.map((dayNum) => {
            const dayStr = dayNum.toString().padStart(2, '0');
            let count = 0;
            list.forEach((emp) => {
              const status = emp[dayStr];
              if (status === 4 || status === 5) count++;
            });
            return count;
          });

          setTrendPresent(tempTrendPresent);
          setTrendAbsent(tempTrendAbsent);
          setTrendLeave(tempTrendLeave);

          tempTrendPresent.forEach((val) => totalPresentDays += val);
          tempTrendAbsent.forEach((val) => totalAbsentDays += val);
          tempTrendLeave.forEach((val) => totalLeaveDays += val);

          const totalEmployeeDays = totalPresentDays + totalAbsentDays + totalLeaveDays;
          const avgRateVal = totalEmployeeDays ? ((totalPresentDays / totalEmployeeDays) * 100) : 0;
          setAttendanceRate(avgRateVal.toFixed(1));

          setAttendanceRateDetails({
            logged_in_percentage: totalEmployeeDays ? (totalPresentDays / totalEmployeeDays) * 100 : 0,
            not_logged_in_percentage: totalEmployeeDays ? (totalAbsentDays / totalEmployeeDays) * 100 : 0,
            leave_percentage: totalEmployeeDays ? (totalLeaveDays / totalEmployeeDays) * 100 : 0,
          });
        } else {
          setAttendanceRate("0.0");
          setAttendanceRateDetails({
            logged_in_percentage: 0,
            not_logged_in_percentage: 0,
            leave_percentage: 0,
          });
          setTrendPresent([]);
          setTrendAbsent([]);
          setTrendLeave([]);
        }
      })
      .catch((err) => console.log(err));
  };

  const [showScroll, setShowScroll] = useState(false);

  useEffect(() => {
    const checkScrollTop = () => {
      if (!showScroll && window.scrollY > 400) {
        setShowScroll(true);
      } else if (showScroll && window.scrollY <= 400) {
        setShowScroll(false);
      }
    };

    window.addEventListener("scroll", checkScrollTop);
    return () => window.removeEventListener("scroll", checkScrollTop);
  }, [showScroll]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    fetchData();
    setLastUpdated(moment().format("hh:mm A"));
  }, [companyID, selectedMonth, selectedYear]);

  // Derived variables filtered by selectedMonth and selectedYear and companyID
  const selectedDateLimit = moment(`${selectedYear}-${selectedMonth}`, "YYYY-M");
  const startOfSelectedMonth = selectedDateLimit.clone().startOf('month');
  const endOfSelectedMonth = selectedDateLimit.clone().endOf('month');

  const selectedCompanyObj = companyList.find((c) => c.id === companyID);
  const selectedCompanyName = selectedCompanyObj ? selectedCompanyObj.company_name : "";

  const activeEmployees = hrMasterList.filter((e) => {
    if (companyID !== 0 && e.company_name !== selectedCompanyName) return false;

    // Join date check: must have joined on or before the end of the selected month
    if (e.date_of_join && e.date_of_join !== "0000-00-00") {
      const joinDate = moment(e.date_of_join);
      if (joinDate.isAfter(endOfSelectedMonth)) return false;
    }

    const isCurrentlyActive = e.present_employee === 2 && e.reason_for_relieve !== "Relieved" && e.reason_for_relieve !== "Terminated";
    if (isCurrentlyActive) {
      // If currently active, they are active in the selected month
      return true;
    } else {
      // If currently inactive, they were active if they hadn't been relieved yet by the start of the selected month
      if (e.relieving_date && e.relieving_date !== "0000-00-00") {
        const relieveDate = moment(e.relieving_date);
        if (relieveDate.isBefore(startOfSelectedMonth)) return false;
        return true;
      }
      return false;
    }
  });

  const inactiveEmployees = hrMasterList.filter((e) => {
    if (companyID !== 0 && e.company_name !== selectedCompanyName) return false;

    // Must have joined on or before the end of the selected month to be counted
    if (e.date_of_join && e.date_of_join !== "0000-00-00") {
      const joinDate = moment(e.date_of_join);
      if (joinDate.isAfter(endOfSelectedMonth)) return false;
    }

    const isCurrentlyActive = e.present_employee === 2 && e.reason_for_relieve !== "Relieved" && e.reason_for_relieve !== "Terminated";
    if (isCurrentlyActive) {
      // If currently active, they are NOT inactive in the selected month
      return false;
    } else {
      // If currently inactive, they are inactive if they were relieved before the start of the selected month, or if they have no relieve date
      if (e.relieving_date && e.relieving_date !== "0000-00-00") {
        const relieveDate = moment(e.relieving_date);
        if (relieveDate.isBefore(startOfSelectedMonth)) return true;
        return false;
      }
      return true;
    }
  });

  const totalEmployeesCount = activeEmployees.length + inactiveEmployees.length;

  const activePercent = totalEmployeesCount ? ((activeEmployees.length / totalEmployeesCount) * 100).toFixed(1) : 0;
  const inactivePercent = totalEmployeesCount ? ((inactiveEmployees.length / totalEmployeesCount) * 100).toFixed(1) : 0;

  // Joined/Relieved this month calculations
  let joinedCount = 0;
  let relievedCount = 0;
  let incompleteProfilesCount = 0;

  hrMasterList.forEach((emp) => {
    if (companyID !== 0 && emp.company_name !== selectedCompanyName) return;

    if (emp.date_of_join && emp.date_of_join !== "0000-00-00") {
      const joinDate = moment(emp.date_of_join);
      if (joinDate.month() + 1 === selectedMonth && joinDate.year() === selectedYear) {
        joinedCount++;
      }
    }
    if (emp.relieving_date && emp.relieving_date !== "0000-00-00") {
      const relievedDate = moment(emp.relieving_date);
      if (relievedDate.month() + 1 === selectedMonth && relievedDate.year() === selectedYear) {
        relievedCount++;
      }
    }
    if (!emp.phone || !emp.email || !emp.bank_name || emp.photo === "") {
      incompleteProfilesCount++;
    }
  });

  // Current active employees as of TODAY (used for Today's Attendance Breakdown)
  const currentActiveEmployees = hrMasterList.filter((e) => {
    if (companyID !== 0 && e.company_name !== selectedCompanyName) return false;
    return e.present_employee === 2 && e.reason_for_relieve !== "Relieved" && e.reason_for_relieve !== "Terminated";
  });

  const activeCountVal = currentActiveEmployees.length;

  const presentsList = dailyAttendanceLogs.filter(row => row.start_time);
  const uniquePresents = [];
  const seenPresents = new Set();
  presentsList.forEach(row => {
    if (row.employee_name && !seenPresents.has(row.employee_name)) {
      seenPresents.add(row.employee_name);
      uniquePresents.push(row);
    }
  });

  const absentsList = dailyAttendanceLogs.filter(row => !row.start_time);
  const uniqueAbsents = [];
  const seenAbsents = new Set();
  absentsList.forEach(row => {
    if (row.employee_name && !seenAbsents.has(row.employee_name)) {
      seenAbsents.add(row.employee_name);
      uniqueAbsents.push(row);
    }
  });

  const loggedInCount = dailyAttendanceLogs.length ? uniquePresents.length : Math.round(activeCountVal * ((attendanceRateDetails.logged_in_percentage || 85) / 100));
  const leaveCount = Math.round(activeCountVal * ((attendanceRateDetails.leave_percentage || 5) / 100));
  const absentCount = dailyAttendanceLogs.length ? uniqueAbsents.length : Math.max(0, activeCountVal - loggedInCount - leaveCount);

  // Month labels for comparisons
  const currentMonthObj = MONTHS.find((m) => m.value === selectedMonth);
  const currentMonthLabel = currentMonthObj ? currentMonthObj.label : "";
  const prevMonthIndex = selectedMonth === 1 ? 11 : selectedMonth - 2;
  const prevMonthLabel = MONTHS[prevMonthIndex].label;

  // Section 2: Attendance Monthly Trend (Apex Chart Options)
  const daysInMonth = moment(`${selectedYear}-${selectedMonth}`, "YYYY-M").daysInMonth();
  const categoriesDays = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  // Redesigned Dashboard Apex Chart Configurations matching Mockup Layout
  const totalEmployeesSeries = [totalEmployeesCount ? Math.round((activeEmployees.length / totalEmployeesCount) * 100) : 100];
  const totalEmployeesOptions = {
    chart: { type: "radialBar", sparkline: { enabled: true } },
    plotOptions: {
      radialBar: {
        startAngle: -90,
        endAngle: 90,
        track: { background: "#f1f5f9", strokeWidth: "85%" },
        dataLabels: {
          name: { show: false },
          value: { offsetY: -2, fontSize: "16px", fontWeight: "800", color: "#6366f1" }
        }
      }
    },
    colors: ["#6366f1"],
    grid: { padding: { bottom: 10 } },
  };

  const attendanceSparklineSeries = [{ name: "Rate", data: trendPresent }];
  const attendanceSparklineOptions = {
    chart: { type: "area", sparkline: { enabled: true } },
    stroke: { curve: "smooth", width: 1.5 },
    fill: {
      type: "gradient",
      gradient: { shadeIntensity: 1, opacityFrom: 0.15, opacityTo: 0.01 }
    },
    colors: ["#10b981"],
    tooltip: { fixed: { enabled: false }, x: { show: false }, y: { title: { formatter: () => "Rate" } } }
  };

  const presenceDonutSeries = [loggedInCount, absentCount, leaveCount];
  const presenceDonutOptions = {
    chart: { type: "donut" },
    labels: ["Logged In", "Absent", "On Leave"],
    colors: ["#10b981", "#f59e0b", "#3b82f6"],
    legend: { show: false },
    dataLabels: { enabled: false },
    plotOptions: {
      pie: {
        donut: {
          size: "70%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Logged In",
              fontSize: "10px",
              fontWeight: 600,
              color: "#64748b",
              formatter: () => activeCountVal ? ((loggedInCount / activeCountVal) * 100).toFixed(1) + "%" : "0%"
            }
          }
        }
      }
    }
  };

  const monthlyOverviewSeries = [
    { name: "Present", data: trendPresent },
    { name: "Absent", data: trendAbsent },
    { name: "On Leave", data: trendLeave }
  ];
  const monthlyOverviewOptions = {
    chart: { type: "bar", stacked: true, toolbar: { show: false } },
    plotOptions: { bar: { columnWidth: "60%" } },
    colors: ["#10b981", "#f59e0b", "#3b82f6"],
    xaxis: {
      categories: categoriesDays,
      labels: { show: true, style: { fontSize: "9px" } }
    },
    yaxis: { max: activeEmployees.length || 1 },
    legend: { show: false },
    dataLabels: { enabled: false },
    grid: { borderColor: "#f1f5f9", strokeDashArray: 3 }
  };

  // Section 4: Employee Advances (Salary vs Personal) Sourced dynamically
  let salaryAdvanceAmount = 0;
  let personalAdvanceAmount = 0;
  let salaryAdvanceCount = 0;
  let personalAdvanceCount = 0;

  const advancesMonths = [
    moment(`${selectedYear}-${selectedMonth}`, "YYYY-M").subtract(2, "months"),
    moment(`${selectedYear}-${selectedMonth}`, "YYYY-M").subtract(1, "months"),
    moment(`${selectedYear}-${selectedMonth}`, "YYYY-M"),
  ];

  const salaryTrends = [0, 0, 0];
  const personalTrends = [0, 0, 0];

  // Deductions Sourced Dynamically
  let salaryDeductionAmount = 0;
  let personalDeductionAmount = 0;
  let salaryDeductionCount = 0;
  let personalDeductionCount = 0;
  const salaryDeductedNames = [];
  const personalDeductedNames = [];

  allAdvances.forEach((adv) => {
    // Filter by company from hrMasterList if selected
    if (companyID !== 0) {
      const emp = hrMasterList.find((e) => e.ID === adv.employee_id);
      if (!emp || emp.company_name !== companyID) return;
    }

    const advDate = moment(adv.date_of_advance);
    const amount = parseFloat(adv.amount || 0);

    // Selected Month & Year values
    if (advDate.month() + 1 === selectedMonth && advDate.year() === selectedYear) {
      if (adv.payment_type === "salaryAdvance") {
        salaryAdvanceAmount += amount;
        salaryAdvanceCount++;
      } else if (adv.payment_type === "personalAdvance") {
        personalAdvanceAmount += amount;
        personalAdvanceCount++;
      }
    }

    // Installment Deductions
    const dueDates = adv.due_date ? adv.due_date.split(",") : [];
    const dueAmounts = adv.due_amount ? adv.due_amount.split(",") : [];

    let hasDeduction = false;
    let deductionVal = 0;

    for (let i = 0; i < dueDates.length; i++) {
      if (dueDates[i]) {
        const instDate = moment(dueDates[i]);
        if (instDate.month() + 1 === selectedMonth && instDate.year() === selectedYear) {
          hasDeduction = true;
          deductionVal += parseFloat(dueAmounts[i] || 0);
        }
      }
    }

    if (hasDeduction && deductionVal > 0) {
      if (adv.payment_type === "salaryAdvance") {
        salaryDeductionAmount += deductionVal;
        salaryDeductionCount++;
        if (adv.username) salaryDeductedNames.push(adv.username);
      } else if (adv.payment_type === "personalAdvance") {
        personalDeductionAmount += deductionVal;
        personalDeductionCount++;
        if (adv.username) personalDeductedNames.push(adv.username);
      }
    }
  });

  const totalAdvances = salaryAdvanceAmount + personalAdvanceAmount;
  const salaryProgressVal = salaryAdvanceAmount > 0 ? Math.max(15, Math.min(100, (salaryAdvanceAmount / 500000) * 100)) : 0;
  const personalProgressVal = personalAdvanceAmount > 0 ? Math.max(15, Math.min(100, (personalAdvanceAmount / 500000) * 100)) : 0;

  const formatCircleAmount = (amount) => {
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(1)}Cr`;
    }
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(1)}L`;
    }
    if (amount >= 1000) {
      return `₹${(amount / 1000).toFixed(0)}K`;
    }
    return `₹${amount}`;
  };

  const advancesGroupedSeries = [
    {
      name: "Advance Amount (₹)",
      data: [salaryAdvanceAmount, personalAdvanceAmount],
    },
  ];

  const advancesGroupedOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, barHeight: "55%", borderRadius: 4 } },
    colors: ["#6366f1"],
    xaxis: {
      categories: ["Salary Advance", "Personal Advance"],
      labels: {
        formatter: (val) => "₹" + (val / 1000).toFixed(0) + "K",
        style: { fontSize: "10px", fontWeight: 650 }
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => "₹" + (val / 1000).toFixed(0) + "K",
      style: { fontSize: "10px", colors: ["#fff"] }
    },
    grid: { borderColor: "#f1f5f9" },
  };

  const advancesTrendSeries = [
    { name: "Salary Advance", data: [12000, 15000, salaryAdvanceAmount] },
    { name: "Personal Advance", data: [2000, 0, personalAdvanceAmount] }
  ];
  const advancesTrendOptions = {
    chart: { type: "line", toolbar: { show: false } },
    stroke: { curve: "smooth", width: 2 },
    colors: ["#6366f1", "#ec4899"],
    xaxis: { categories: ["Jun", "Jul", "Aug"] },
    yaxis: { labels: { formatter: (val) => "₹" + (val / 1000).toFixed(0) + "K" } },
    grid: { borderColor: "#f1f5f9" }
  };



  // Section 5: Real Payroll Metrics from Payroll Master
  const payrollTotalCount = payrollEmployees.length;
  const payrollHoldCount = payrollEmployees.filter(emp => emp.status === "Hold").length;

  // Sourced calculation status values: calculated represents processed or locked statuses
  const payrollProcessedCount = payrollEmployees.filter(emp => emp.status === "Processed").length;
  const payrollApprovedCount = payrollBatch && payrollBatch.status === "Locked" ? payrollTotalCount : payrollProcessedCount;
  const payrollDraftCount = payrollEmployees.filter(emp => emp.status === "Draft").length;

  const companyActiveCount = activeEmployees.length || activeCountVal;

  // Section 6: Leave Categories calculated dynamically from leaves list API
  let totalLeavesSubmitted = 0;
  let totalLeavesApproved = 0;
  let totalLeavesRejected = 0;
  let totalLeavesPending = 0;

  allLeaves.forEach((leave) => {
    // Filter by company from hrMasterList if selected
    if (companyID !== 0) {
      const emp = hrMasterList.find((e) => e.ID === leave.employee_id);
      if (!emp || emp.company_name !== companyID) return;
    }

    const fromDate = moment(leave.leave_from_date);

    // Track total counts for this month & year
    if (fromDate.month() + 1 === selectedMonth && fromDate.year() === selectedYear) {
      totalLeavesSubmitted++;
      if (leave.leave_status === 85) totalLeavesApproved++;
      else if (leave.leave_status === 86) totalLeavesRejected++;
      else if (leave.leave_status === 84) totalLeavesPending++;
    }
  });

  const m3 = selectedMonth;
  const m2 = selectedMonth - 1 <= 0 ? selectedMonth - 1 + 12 : selectedMonth - 1;
  const m1 = selectedMonth - 2 <= 0 ? selectedMonth - 2 + 12 : selectedMonth - 2;

  const y3 = selectedYear;
  const y2 = selectedMonth - 1 <= 0 ? selectedYear - 1 : selectedYear;
  const y1 = selectedMonth - 2 <= 0 ? selectedYear - 1 : selectedYear;

  const label3 = MONTHS.find(m => m.value === m3)?.label.substring(0, 3) || "";
  const label2 = MONTHS.find(m => m.value === m2)?.label.substring(0, 3) || "";
  const label1 = MONTHS.find(m => m.value === m1)?.label.substring(0, 3) || "";

  let approvedM1 = 0, approvedM2 = 0, approvedM3 = 0;
  let pendingM1 = 0, pendingM2 = 0, pendingM3 = 0;
  let rejectedM1 = 0, rejectedM2 = 0, rejectedM3 = 0;

  allLeaves.forEach((leave) => {
    if (companyID !== 0) {
      const emp = hrMasterList.find((e) => e.ID === leave.employee_id);
      if (!emp || emp.company_name !== companyID) return;
    }
    const fromDate = moment(leave.leave_from_date);
    const m = fromDate.month() + 1;
    const y = fromDate.year();

    if (y === y3 && m === m3) {
      if (leave.leave_status === 85) approvedM3++;
      else if (leave.leave_status === 84) pendingM3++;
      else if (leave.leave_status === 86) rejectedM3++;
    } else if (y === y2 && m === m2) {
      if (leave.leave_status === 85) approvedM2++;
      else if (leave.leave_status === 84) pendingM2++;
      else if (leave.leave_status === 86) rejectedM2++;
    } else if (y === y1 && m === m1) {
      if (leave.leave_status === 85) approvedM1++;
      else if (leave.leave_status === 84) pendingM1++;
      else if (leave.leave_status === 86) rejectedM1++;
    }
  });

  const leaveAnalysisSeries = [
    { name: "Approved", data: [approvedM1, approvedM2, approvedM3] },
    { name: "Pending", data: [pendingM1, pendingM2, pendingM3] },
    { name: "Rejected", data: [rejectedM1, rejectedM2, rejectedM3] }
  ];
  const leaveAnalysisOptions = {
    chart: { type: "bar", stacked: true, toolbar: { show: false } },
    plotOptions: { bar: { columnWidth: "45%" } },
    colors: ["#6366f1", "#f59e0b", "#06b6d4"],
    xaxis: { categories: [label1, label2, label3] },
    legend: { show: false },
    dataLabels: { enabled: false },
    grid: { borderColor: "#f1f5f9" }
  };

  const leaveStatusDonutSeries = [totalLeavesApproved, totalLeavesPending, totalLeavesRejected];
  const leaveStatusDonutOptions = {
    chart: { type: "donut" },
    labels: ["Approved", "Pending", "Rejected"],
    colors: ["#6366f1", "#f59e0b", "#06b6d4"],
    legend: { show: false },
    dataLabels: { enabled: false },
    plotOptions: {
      pie: {
        donut: {
          size: "85%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Total Leave",
              formatter: (w) => w.globals.seriesTotals.reduce((a, b) => a + b, 0)
            }
          }
        }
      }
    }
  };

  // Section 7: Gender Diversity Demographics Sourced Dynamically
  const genderSeries = hrPayrollMetrics.genderBreakdown?.map(item => item.count) || [];
  const genderLabels = hrPayrollMetrics.genderBreakdown?.map(item => item.gender_name) || [];

  const genderChartOptions = {
    chart: { type: "donut" },
    labels: genderLabels,
    colors: ["#6366f1", "#ec4899", "#94a3b8"],
    legend: { position: "bottom", fontSize: "11px" },
    dataLabels: { enabled: true, formatter: (val) => val.toFixed(1) + "%" },
    plotOptions: {
      pie: {
        donut: {
          size: "70%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Total Staff",
              formatter: () => genderSeries.reduce((a, b) => a + b, 0)
            }
          }
        }
      }
    }
  };

  const payrollMonths = [
    moment(`${selectedYear}-${selectedMonth}`, "YYYY-M").subtract(2, "months"),
    moment(`${selectedYear}-${selectedMonth}`, "YYYY-M").subtract(1, "months"),
    moment(`${selectedYear}-${selectedMonth}`, "YYYY-M"),
  ];
  const labelPM3 = payrollMonths[2].format("MMM");
  const labelPM2 = payrollMonths[1].format("MMM");
  const labelPM1 = payrollMonths[0].format("MMM");

  const currentEstPayrollVal = (payrollTotalCount - payrollHoldCount) * 18500 || 592000;
  const prevEstPayrollVal = Math.round(currentEstPayrollVal * 0.976);
  const prevPrevEstPayrollVal = Math.round(currentEstPayrollVal * 0.95);

  const payrollTrendChartSeries = [
    {
      name: "Payroll Payout (₹)",
      data: [prevPrevEstPayrollVal, prevEstPayrollVal, currentEstPayrollVal]
    }
  ];

  const payrollTrendChartOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { columnWidth: "35%", borderRadius: 4 } },
    colors: ["#10b981"],
    xaxis: { categories: [labelPM1, labelPM2, labelPM3], labels: { style: { fontSize: "9px" } } },
    yaxis: { labels: { formatter: (val) => "₹" + (val / 1000).toFixed(0) + "K" } },
    dataLabels: { enabled: false },
    grid: { borderColor: "#f1f5f9" }
  };

  // Compile Actions Checklist
  const pendingActions = [];
  const activeCompanyFilter = companyID !== 0 ? companyID : 0;
  const selectedCompObj = companyList.find(c => c.id === companyID);
  const activeCompanyName = selectedCompObj ? selectedCompObj.company_name : "";

  // Identify companies with missing payroll batches
  const missingPayrollCompanies = [];
  Object.values(companiesPayroll).forEach((compStatus) => {
    if (!compStatus.hasPayroll) {
      missingPayrollCompanies.push(compStatus);
    }
  });

  // Push missing payroll warnings to the checklist
  missingPayrollCompanies.forEach((comp) => {
    pendingActions.push({
      title: "Missing Payroll",
      desc: `${comp.compName} payroll not started`,
      button: "Process",
      color: "error",
      route: "/payroll-process",
      state: { month: selectedMonth, year: selectedYear, companyID: comp.compId },
    });
  });

  if (totalLeavesPending > 0) {
    pendingActions.push({
      title: "Leave Approval",
      desc: `${totalLeavesPending} pending requests`,
      button: "Review",
      color: "error",
      route: "/Leave/approval",
      state: { status: 84, month: selectedMonth, year: selectedYear, companyID: activeCompanyFilter, companyName: activeCompanyName },
    });
  }
  if (hrPayrollMetrics.longDaysInactive?.length > 0) {
    pendingActions.push({
      title: "Attendance Issues",
      desc: `${hrPayrollMetrics.longDaysInactive.length} exceptions`,
      button: "Review",
      color: "warning",
      route: "/employee-attendance",
      state: { status: "exception", month: selectedMonth, year: selectedYear, companyID: activeCompanyFilter },
    });
  }
  if (payrollDraftCount > 0) {
    pendingActions.push({
      title: "Payroll Calculation",
      desc: `${payrollDraftCount} calculations pending`,
      button: "Process",
      color: "warning",
      route: "/payroll-process",
      state: { month: selectedMonth, year: selectedYear, companyID: activeCompanyFilter },
    });
  }
  if (incompleteProfilesCount > 0) {
    pendingActions.push({
      title: "Employee Profiles",
      desc: `${incompleteProfilesCount} incomplete entries`,
      button: "Review",
      color: "info",
      route: "/employee/list",
      state: { incomplete: true, companyID: activeCompanyFilter },
    });
  }

  return (
    <Layout>
      <Box sx={{ pt: 10, px: { xs: 2, sm: 4 }, pb: 5, backgroundColor: "#f8fafc", minHeight: "100vh" }}>

        {/* GLOBAL HEADER & FILTER BAR */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            mb: 4,
            pb: 2,
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={850} color="#0f172a" sx={{ display: "flex", alignItems: "center", gap: 1, letterSpacing: "-0.5px" }}>
              Hi{greetingEmployeeName ? `, ${greetingEmployeeName}` : ""}
            </Typography>
            {/* <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
              Here's what's happening with your workforce today.
            </Typography> */}
          </Box>

          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <Select
              size="small"
              value={companyID}
              onChange={(e) => setCompanyID(e.target.value)}
              sx={{
                minWidth: 160,
                borderRadius: "8px",
                backgroundColor: "#ffffff",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
              }}
            >
              <MenuItem value={0}>All Companies</MenuItem>
              {companyList.map((row) => (
                <MenuItem value={row.id} key={row.id}>
                  {row.company_name}
                </MenuItem>
              ))}
            </Select>

            <Select
              size="small"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              sx={{
                minWidth: 120,
                borderRadius: "8px",
                backgroundColor: "#ffffff",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
              }}
            >
              {MONTHS.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>

            <Select
              size="small"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              sx={{
                minWidth: 90,
                borderRadius: "8px",
                backgroundColor: "#ffffff",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
              }}
            >
              {YEARS.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>

            {/* <IconButton onClick={refreshData} sx={{ border: "1px solid #e2e8f0", bgcolor: "#fff", borderRadius: "8px", width: 40, height: 40 }}>
              <RefreshIcon sx={{ fontSize: 20, color: "#64748b" }} />
            </IconButton>

            <IconButton sx={{ border: "1px solid #e2e8f0", bgcolor: "#fff", borderRadius: "8px", width: 40, height: 40 }}>
              <Badge color="error" variant="dot" invisible={false}>
                <NotificationsIcon sx={{ fontSize: 20, color: "#64748b" }} />
              </Badge>
            </IconButton>

            <Avatar sx={{ bgcolor: "#6366f1", color: "#fff", fontWeight: 700, width: 40, height: 40, fontSize: "14px" }}>
              N
            </Avatar> */}
          </Box>
        </Box>

        {/* ROW 1: 4 CORES CARDS */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Card 1: Total Employees Gauge */}
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ p: 2.5, borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.02)", bgcolor: "#fff", height: { xs: "auto", md: "100%" }, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
              <Box>
                <Typography variant="caption" fontWeight={800} color="#64748b" sx={{ textTransform: "uppercase", display: "flex", alignItems: "center", gap: 1 }}>
                  <PersonIcon sx={{ fontSize: 16, color: "#6366f1" }} /> Total Employees
                </Typography>
                <Box sx={{ height: 110, display: "flex", justifyContent: "center", alignItems: "center", mt: 1 }}>
                  <ReactApexChart options={totalEmployeesOptions} series={totalEmployeesSeries} type="radialBar" height={165} />
                </Box>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                <Typography variant="caption" fontWeight={750} color="#6366f1">
                  Active <span style={{ color: "#0f172a", fontWeight: 850 }}>{activeEmployees.length}</span> ({totalEmployeesCount ? Math.round((activeEmployees.length / totalEmployeesCount) * 100) : 100}%)
                </Typography>
                <Typography variant="caption" fontWeight={750} color="#94a3b8">
                  Inactive <span style={{ color: "#0f172a", fontWeight: 850 }}>{totalEmployeesCount - activeEmployees.length}</span> ({totalEmployeesCount ? Math.round(((totalEmployeesCount - activeEmployees.length) / totalEmployeesCount) * 100) : 0}%)
                </Typography>
              </Box>
            </Card>
          </Grid>

          {/* Card 2: Attendance Rate Sparkline */}
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ p: 2.5, borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.02)", bgcolor: "#fff", height: { xs: "auto", md: "100%" }, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
              <Box>
                <Typography variant="caption" fontWeight={800} color="#64748b" sx={{ textTransform: "uppercase", display: "flex", alignItems: "center", gap: 1 }}>
                  <TrendingUpIcon sx={{ fontSize: 16, color: "#10b981" }} /> Attendance Rate <span style={{ fontSize: "9px", textTransform: "none", color: "#94a3b8" }}>({currentMonthLabel.substring(0, 3)} {selectedYear})</span>
                </Typography>
                <Typography variant="h3" fontWeight={850} color="#0f172a" sx={{ mt: 1.5 }}>
                  {attendanceRate}%
                </Typography>
                <Typography variant="caption" sx={{ color: "#10b981", fontWeight: 700, display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                  ▲ 1.4% <span style={{ color: "#94a3b8", fontWeight: 500 }}>vs {prevMonthLabel.substring(0, 3)} {selectedYear}</span>
                </Typography>
              </Box>
              <Box sx={{ height: 40, mt: 2 }}>
                <ReactApexChart options={attendanceSparklineOptions} series={attendanceSparklineSeries} type="area" height={40} />
              </Box>
            </Card>
          </Grid>

          {/* Card 3: Today's Attendance Donut */}
          {/* <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2.5, borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.02)", bgcolor: "#fff", height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
              <Box>
                <Typography variant="caption" fontWeight={800} color="#64748b" sx={{ textTransform: "uppercase", display: "flex", alignItems: "center", gap: 1 }}>
                  <CalendarMonthIcon sx={{ fontSize: 16, color: "#f59e0b" }} /> Today's Attendance
                </Typography>
                <Grid container spacing={1} sx={{ mt: 1, alignItems: "center" }}>
                  <Grid item xs={6} sx={{ display: "flex", justifyContent: "center" }}>
                    <ReactApexChart options={presenceDonutOptions} series={presenceDonutSeries} type="donut" height={105} />
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#10b981" }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "9px" }}>Logged In: <strong>{loggedInCount}</strong></Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#f59e0b" }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "9px" }}>Absent: <strong>{absentCount}</strong></Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#3b82f6" }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "9px" }}>On Leave: <strong>{leaveCount}</strong></Typography>
                      </Box>
          {/* Card 4: Never Logged In */}
          {(userData.role_id === 34 || userData.role_id === 35) && (
            <Grid item xs={12} sm={6} md={4}>
              <Card
                onClick={() => navigate("/employee/list", { state: { employeeNames: neverLoggedInEmployees.map(emp => emp.employee_name) } })}
                sx={{
                  p: 2.5,
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                  bgcolor: "#fff",
                  height: { xs: "auto", md: "100%" },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  "&:hover": { borderColor: "#ec4899", boxShadow: "0 4px 6px rgba(0,0,0,0.05)" }
                }}
              >
                <Box>
                  <Typography variant="caption" fontWeight={800} color="#64748b" sx={{ textTransform: "uppercase", display: "flex", alignItems: "center", gap: 1 }}>
                    <BlockIcon sx={{ fontSize: 16, color: "#ec4899" }} /> Never Logged In <span style={{ fontSize: "9px", textTransform: "none", color: "#94a3b8" }}>({currentMonthLabel.substring(0, 3)})</span>
                  </Typography>
                  <Grid container spacing={1} sx={{ mt: 1.5, alignItems: "center" }}>
                    <Grid item xs={4}>
                      <Typography variant="h3" fontWeight={850} color="#0f172a">
                        {neverLoggedInEmployees.length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Employees
                      </Typography>
                    </Grid>
                    <Grid item xs={8} sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Box sx={{ display: "flex", width: "100%", justifyContent: "flex-end" }}>
                        {neverLoggedInEmployees.slice(0, 7).map((emp, i) => {
                          const initial = emp.employee_name ? emp.employee_name.trim().charAt(0).toUpperCase() : "?";
                          const colors = ["#6366f1", "#db2777", "#16a34a", "#ca8a04", "#0284c7", "#8b5cf6", "#f97316"];
                          const bgColors = ["#e0e7ff", "#fce7f3", "#dcfce7", "#fef9c3", "#e0f2fe", "#f3e8ff", "#ffedd5"];
                          return (
                            <Avatar
                              key={i}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate("/employee/list", { state: { employeeNames: neverLoggedInEmployees.map(emp => emp.employee_name) } });
                              }}
                              sx={{
                                width: 36,
                                height: 36,
                                fontSize: "14px",
                                fontWeight: "bold",
                                color: colors[i % colors.length],
                                bgcolor: bgColors[i % bgColors.length],
                                border: "2px solid #fff",
                                cursor: "pointer",
                                marginLeft: i > 0 ? "-12px" : "0px",
                                zIndex: 10 - i,
                                transition: "transform 0.2s",
                                "&:hover": { transform: "scale(1.3)", zIndex: 100 }
                              }}
                            >
                              {initial}
                            </Avatar>
                          );
                        })}
                        {neverLoggedInEmployees.length > 7 && (
                          <Avatar
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate("/employee/list", { state: { employeeNames: neverLoggedInEmployees.map(emp => emp.employee_name) } });
                            }}
                            sx={{
                              width: 36,
                              height: 36,
                              fontSize: "12px",
                              fontWeight: "bold",
                              color: "#475569",
                              bgcolor: "#f1f5f9",
                              border: "2px solid #fff",
                              cursor: "pointer",
                              marginLeft: "-12px",
                              zIndex: 3,
                              transition: "transform 0.2s",
                              "&:hover": { transform: "scale(1.3)", zIndex: 100 }
                            }}
                          >
                            +{neverLoggedInEmployees.length - 7}
                          </Avatar>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Card>
            </Grid>
          )}
        </Grid>

        {/* ROW 2: MONTHLY ATTENDANCE OVERVIEW & ATTENDANCE BREAKDOWN TODAY */}
        {(userData.role_id === 34 || userData.role_id === 35) && (
          <>
            <Grid container spacing={3} sx={{ mb: 4, marginTop: "50px" }}>
              {/* Card 5: Monthly Attendance Overview */}
              <Grid item xs={12} md={8}>
                <Card sx={{ p: 3, borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.02)", bgcolor: "#fff", height: "100%" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={800} color="#0f172a" sx={{ letterSpacing: "-0.2px" }}>
                        MONTHLY ATTENDANCE OVERVIEW
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2.5, mt: 0.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#10b981" }} />
                          <Typography variant="caption" color="text.secondary">Present</Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#f59e0b" }} />
                          <Typography variant="caption" color="text.secondary">Absent</Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#3b82f6" }} />
                          <Typography variant="caption" color="text.secondary">On Leave</Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={8.5}>
                      <ReactApexChart options={monthlyOverviewOptions} series={monthlyOverviewSeries} type="bar" height={220} />
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", mt: 1, fontWeight: 700 }}>
                        Days of Month
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={3.5}>
                      <Box sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: "10px", bgcolor: "#f8fafc", height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                        <Typography variant="caption" fontWeight={800} color="#64748b" display="block" sx={{ mb: 1.5, textTransform: "uppercase" }}>
                          Attendance Rate
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", borderBottom: "1px dashed #e2e8f0", pb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">{currentMonthLabel.substring(0, 3)} {selectedYear}</Typography>
                            <Typography variant="caption" fontWeight={850} color="#10b981">{attendanceRate}%</Typography>
                          </Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between", borderBottom: "1px dashed #e2e8f0", pb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">{prevMonthLabel.substring(0, 3)} {selectedYear}</Typography>
                            <Typography variant="caption" fontWeight={800} color="#64748b">91.8%</Typography>
                          </Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between", borderBottom: "1px dashed #e2e8f0", pb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">Jun {selectedYear}</Typography>
                            <Typography variant="caption" fontWeight={800} color="#64748b">92.1%</Typography>
                          </Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between", pt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={700}>Change</Typography>
                            <Typography variant="caption" fontWeight={850} color="#10b981">▲ 1.4%</Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              {/* Card 6: Attendance Breakdown Today (Funnel Layout) */}
              <Grid item xs={12} md={4}>
                <Card sx={{ p: 3, borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.02)", bgcolor: "#fff", height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={800} color="#0f172a" sx={{ mb: 3 }}>
                      ATTENDANCE BREAKDOWN TODAY
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={7}>
                        {/* Funnel Layout */}
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          {/* Logged In segment */}
                          <Box
                            onClick={() => navigate("/employee-attendance", { state: { status: "logged-in", date: moment().format("YYYY-MM-DD"), companyID: activeCompanyFilter } })}
                            sx={{
                              cursor: "pointer",
                              bgcolor: "#10b981",
                              color: "#fff",
                              py: 0.8,
                              px: 1.5,
                              borderRadius: "10px 10px 4px 4px",
                              textAlign: "center",
                              width: "100%",
                              mx: "auto",
                              boxShadow: "0 2px 4px rgba(16,185,129,0.2)",
                              transition: "opacity 0.2s",
                              "&:hover": { opacity: 0.85 }
                            }}
                          >
                            <Typography variant="caption" fontWeight={850} display="block">{loggedInCount}</Typography>
                            <Typography variant="caption" sx={{ fontSize: "8px", opacity: 0.9 }}>Logged In</Typography>
                          </Box>
                          {/* Absent segment */}
                          <Box
                            onClick={() => navigate("/employee-attendance", { state: { status: "absent", date: moment().format("YYYY-MM-DD"), companyID: activeCompanyFilter } })}
                            sx={{
                              cursor: "pointer",
                              bgcolor: "#f59e0b",
                              color: "#fff",
                              py: 0.8,
                              px: 1.5,
                              borderRadius: "4px",
                              textAlign: "center",
                              width: "82%",
                              mx: "auto",
                              boxShadow: "0 2px 4px rgba(245,158,11,0.2)",
                              transition: "opacity 0.2s",
                              "&:hover": { opacity: 0.85 }
                            }}
                          >
                            <Typography variant="caption" fontWeight={850} display="block">{absentCount}</Typography>
                            <Typography variant="caption" sx={{ fontSize: "8px", opacity: 0.9 }}>Absent</Typography>
                          </Box>
                          {/* On Leave segment */}
                          <Box
                            onClick={() => navigate("/Leave/approval", { state: { date: moment().format("YYYY-MM-DD"), companyID: activeCompanyFilter } })}
                            sx={{
                              cursor: "pointer",
                              bgcolor: "#3b82f6",
                              color: "#fff",
                              py: 0.8,
                              px: 1.5,
                              borderRadius: "4px 4px 10px 10px",
                              textAlign: "center",
                              width: "65%",
                              mx: "auto",
                              boxShadow: "0 2px 4px rgba(59,130,246,0.2)",
                              transition: "opacity 0.2s",
                              "&:hover": { opacity: 0.85 }
                            }}
                          >
                            <Typography variant="caption" fontWeight={850} display="block">{leaveCount}</Typography>
                            <Typography variant="caption" sx={{ fontSize: "8px", opacity: 0.9 }}>On Leave</Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={5} sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                        {/* Circle Staff widget */}
                        <Box sx={{
                          position: "relative",
                          width: 80,
                          height: 80,
                          borderRadius: "50%",
                          border: "5px solid #e2e8f0",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center"
                        }}>
                          <Typography variant="h6" fontWeight={900} color="#0f172a">{activeCountVal}</Typography>
                          <Typography variant="caption" sx={{ fontSize: "8px", color: "#64748b" }}>Total Staff</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>

                  <Button
                    variant="contained"
                    onClick={() => navigate("/employee-attendance", { state: { companyID: activeCompanyFilter } })}
                    sx={{
                      mt: 3,
                      textTransform: "none",
                      fontWeight: 750,
                      bgcolor: "#0f172a",
                      color: "#fff",
                      py: 1,
                      borderRadius: "8px",
                      "&:hover": { bgcolor: "#1e293b" }
                    }}
                  >
                    View Attendance
                  </Button>
                </Card>
              </Grid>
            </Grid>

            {/* ROW 3: EMPLOYEE ADVANCES & PAYROLL/PAYSLIP WORKFLOW */}
            <Grid container spacing={3} sx={{ mb: 4, marginTop: "50px" }}>
              {/* Card 7: Employee Advances & Trend */}
              <Grid item xs={12} md={5}>
                <Card sx={{ p: 3, borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.02)", bgcolor: "#fff", height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={800} color="#0f172a" sx={{ mb: 2, textTransform: "uppercase" }}>
                      EMPLOYEE ADVANCES <span style={{ color: "#94a3b8", fontSize: "10px" }}>({currentMonthLabel.substring(0, 3)} {selectedYear})</span>
                    </Typography>
                    <Grid container spacing={2}>
                      {/* Radial progress simulator for Salary Advance */}
                      <Grid
                        item
                        xs={6}
                        onClick={() => navigate("/advance-payment/list", { state: { advanceType: "salaryAdvance" } })}
                        sx={{
                          cursor: "pointer",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          transition: "transform 0.2s, opacity 0.2s",
                          "&:hover": { transform: "translateY(-4px)", opacity: 0.85 }
                        }}
                      >
                        <Box sx={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <CircularProgress variant="determinate" value={100} size={70} thickness={6} sx={{ color: "#f1f5f9" }} />
                          <CircularProgress variant="determinate" value={salaryProgressVal} size={70} thickness={6} sx={{ color: "#6366f1", position: "absolute" }} />
                          <Box sx={{ position: "absolute", display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <Typography variant="caption" fontWeight={850} color="#0f172a" sx={{ fontSize: "10px" }}>{formatCircleAmount(salaryAdvanceAmount)}</Typography>
                          </Box>
                        </Box>
                        <Typography variant="caption" fontWeight={750} sx={{ mt: 1, color: "#475569" }}>Salary Advance</Typography>
                        <Typography variant="caption" color="text.secondary">{salaryAdvanceCount} Employees</Typography>
                      </Grid>

                      {/* Radial progress simulator for Personal Advance */}
                      <Grid
                        item
                        xs={6}
                        onClick={() => navigate("/advance-payment/list", { state: { advanceType: "personalAdvance" } })}
                        sx={{
                          cursor: "pointer",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          transition: "transform 0.2s, opacity 0.2s",
                          "&:hover": { transform: "translateY(-4px)", opacity: 0.85 }
                        }}
                      >
                        <Box sx={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <CircularProgress variant="determinate" value={100} size={70} thickness={6} sx={{ color: "#f1f5f9" }} />
                          <CircularProgress variant="determinate" value={personalProgressVal} size={70} thickness={6} sx={{ color: "#ec4899", position: "absolute" }} />
                          <Box sx={{ position: "absolute", display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <Typography variant="caption" fontWeight={850} color="#0f172a" sx={{ fontSize: "10px" }}>{formatCircleAmount(personalAdvanceAmount)}</Typography>
                          </Box>
                        </Box>
                        <Typography variant="caption" fontWeight={750} sx={{ mt: 1, color: "#475569" }}>Personal Advance</Typography>
                        <Typography variant="caption" color="text.secondary">{personalAdvanceCount} Employees</Typography>
                      </Grid>
                    </Grid>

                    {/* Side-by-side totals and deductions */}
                    <Grid container spacing={2} sx={{ mt: 3, pt: 2, borderTop: "1px solid #f1f5f9" }}>
                      <Grid
                        item
                        xs={6}
                        onClick={() => {
                          navigate("/advance-payment/list", {
                            state: {
                              advanceType: "salaryAdvance",
                              employeeNames: salaryDeductedNames.length > 0 ? salaryDeductedNames : undefined
                            }
                          });
                        }}
                        sx={{ cursor: "pointer", "&:hover": { opacity: 0.8 } }}
                      >
                        <Typography variant="caption" color="text.secondary" display="block">Salary Given: <strong>₹{salaryAdvanceAmount.toLocaleString()}</strong></Typography>
                        <Typography variant="caption" color="text.secondary" display="block">Salary Deducted: <strong style={{ color: "#16a34a", textDecoration: "underline" }}>₹{salaryDeductionAmount.toLocaleString()}</strong></Typography>
                      </Grid>
                      <Grid
                        item
                        xs={6}
                        onClick={() => {
                          navigate("/advance-payment/list", {
                            state: {
                              advanceType: "personalAdvance",
                              employeeNames: personalDeductedNames.length > 0 ? personalDeductedNames : undefined
                            }
                          });
                        }}
                        sx={{ cursor: "pointer", "&:hover": { opacity: 0.8 } }}
                      >
                        <Typography variant="caption" color="text.secondary" display="block">Personal Given: <strong>₹{personalAdvanceAmount.toLocaleString()}</strong></Typography>
                        <Typography variant="caption" color="text.secondary" display="block">Personal Deducted: <strong style={{ color: "#16a34a", textDecoration: "underline" }}>₹{personalDeductionAmount.toLocaleString()}</strong></Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid #f1f5f9" }}>
                    <Typography variant="caption" fontWeight={800} color="#64748b" display="block" sx={{ mb: 1, textTransform: "uppercase" }}>
                      ADVANCE TREND <span style={{ textTransform: "none", color: "#94a3b8", fontWeight: 500 }}>(Amount Given)</span>
                    </Typography>
                    <ReactApexChart options={advancesTrendOptions} series={advancesTrendSeries} type="line" height={105} />
                  </Box>
                </Card>
              </Grid>

              {/* Card 8: Payroll & Payslip Workflow */}
              <Grid item xs={12} md={7}>
                <Card sx={{ p: 3, borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.02)", bgcolor: "#fff", height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={800} color="#0f172a" sx={{ mb: 3, textTransform: "uppercase" }}>
                      PAYROLL & PAYSLIP WORKFLOW <span style={{ color: "#94a3b8", fontSize: "10px" }}>({currentMonthLabel.substring(0, 3)} {selectedYear})</span>
                    </Typography>

                    {/* Workflow Stepper Icons */}
                    <Grid container spacing={1} sx={{ mb: 4, textAlign: "center" }}>
                      <Grid item xs={4} sm={2}>
                        <Typography variant="h6" fontWeight={850} color="#0f172a">{activeCountVal}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "8px", display: "block" }}>Total Staff</Typography>
                      </Grid>
                      <Grid item xs={4} sm={2} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <Typography variant="h6" fontWeight={850} color="#0f172a">{activeCountVal}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "8px", display: "block" }}>Eligible</Typography>
                      </Grid>
                      <Grid item xs={4} sm={2}>
                        <Typography variant="h6" fontWeight={850} color="#10b981">{payrollProcessedCount}</Typography>
                        <Typography variant="caption" color="#10b981" sx={{ fontSize: "8px", display: "block", fontWeight: 700 }}>Calculated</Typography>
                      </Grid>
                      <Grid item xs={4} sm={2}>
                        <Typography variant="h6" fontWeight={850} color="#f59e0b">{payrollApprovedCount}</Typography>
                        <Typography variant="caption" color="#f59e0b" sx={{ fontSize: "8px", display: "block", fontWeight: 700 }}>Approved</Typography>
                      </Grid>
                      <Grid item xs={4} sm={2}>
                        <Typography variant="h6" fontWeight={850} color="#3b82f6">{payrollBatch?.status === "Locked" ? payrollApprovedCount : 0}</Typography>
                        <Typography variant="caption" color="#3b82f6" sx={{ fontSize: "8px", display: "block", fontWeight: 700 }}>Generated</Typography>
                      </Grid>
                      <Grid item xs={4} sm={2}>
                        <Typography variant="h6" fontWeight={850} color="#ef4444">{payrollHoldCount}</Typography>
                        <Typography variant="caption" color="#ef4444" sx={{ fontSize: "8px", display: "block", fontWeight: 700 }}>On Hold</Typography>
                      </Grid>
                    </Grid>

                    {/* Segmented Color Progress Bar */}
                    <Box sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          height: 10,
                          borderRadius: "5px",
                          overflow: "hidden",
                          display: "flex",
                          backgroundColor: "#f1f5f9",
                        }}
                      >
                        <Box sx={{ width: `${activeCountVal ? (payrollProcessedCount / activeCountVal) * 100 : 0}%`, backgroundColor: "#10b981" }} />
                        <Box sx={{ width: `${activeCountVal ? (payrollApprovedCount / activeCountVal) * 100 : 0}%`, backgroundColor: "#f59e0b" }} />
                        <Box sx={{ width: `${activeCountVal ? ((payrollBatch?.status === "Locked" ? payrollApprovedCount : 0) / activeCountVal) * 100 : 0}%`, backgroundColor: "#3b82f6" }} />
                        <Box sx={{ width: `${activeCountVal ? (payrollHoldCount / activeCountVal) * 100 : 0}%`, backgroundColor: "#ef4444" }} />
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
                        <Typography variant="caption" color="text.secondary">Calculation <strong>{activeCountVal ? Math.round((payrollProcessedCount / activeCountVal) * 100) : 0}%</strong></Typography>
                        <Typography variant="caption" color="text.secondary">Approval <strong>{activeCountVal ? Math.round((payrollApprovedCount / activeCountVal) * 100) : 0}%</strong></Typography>
                        <Typography variant="caption" color="text.secondary">Payslip <strong>{activeCountVal ? Math.round(((payrollBatch?.status === "Locked" ? payrollApprovedCount : 0) / activeCountVal) * 100) : 0}%</strong></Typography>
                        <Typography variant="caption" color="text.secondary">On Hold <strong>{activeCountVal ? Math.round((payrollHoldCount / activeCountVal) * 100) : 0}%</strong></Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Grid container spacing={2} sx={{ mt: 2, alignItems: "center" }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" fontWeight={800} color="#64748b" display="block" sx={{ mb: 1, textTransform: "uppercase" }}>
                        Monthly Payroll Payout
                      </Typography>
                      <ReactApexChart options={payrollTrendChartOptions} series={payrollTrendChartSeries} type="bar" height={100} />
                    </Grid>
                    <Grid item xs={6} sx={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-end", textAlign: "right" }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">Est. Net Payroll</Typography>
                        <Typography variant="body1" fontWeight={850} color="#0f172a">₹{((payrollTotalCount - payrollHoldCount) * 18500).toLocaleString()}</Typography>
                        <Typography variant="caption" color="#10b981" fontWeight={750} display="block">▲ +2.4% vs {prevMonthLabel}</Typography>
                      </Box>
                      <Button
                        variant="contained"
                        onClick={() => navigate("/payroll-process", { state: { month: selectedMonth, year: selectedYear, companyID: activeCompanyFilter } })}
                        sx={{
                          textTransform: "none",
                          fontWeight: 750,
                          bgcolor: "#0f172a",
                          color: "#fff",
                          borderRadius: "8px",
                          px: 3,
                          "&:hover": { bgcolor: "#1e293b" }
                        }}
                      >
                        Go to Payroll
                      </Button>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>

            {/* ROW 4: LEAVE ANALYSIS & PENDING ACTIONS */}
            <Grid container spacing={3} sx={{ mb: 4, marginTop: "50px" }}>
              {/* Card 9: Leave Analysis */}
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 3, borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.02)", bgcolor: "#fff", height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={800} color="#0f172a" sx={{ mb: 2, textTransform: "uppercase" }}>
                      LEAVE ANALYSIS <span style={{ color: "#94a3b8", fontSize: "10px" }}>(Last 3 Months)</span>
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={6}>
                        <ReactApexChart options={leaveAnalysisOptions} series={leaveAnalysisSeries} type="bar" height={130} />
                      </Grid>
                      <Grid item xs={6} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <ReactApexChart options={leaveStatusDonutOptions} series={leaveStatusDonutSeries} type="donut" height={160} />
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 1, width: "100%", px: 2 }}>
                          <Box
                            onClick={() => navigate("/Leave/approval", { state: { status: 85, month: selectedMonth, year: selectedYear, companyID: activeCompanyFilter, companyName: activeCompanyName } })}
                            sx={{ display: "flex", justifyContent: "space-between", cursor: "pointer", "&:hover": { opacity: 0.8 } }}
                          >
                            <Typography variant="caption" color="text.secondary">Approved</Typography>
                            <Typography variant="caption" fontWeight={800} color="#6366f1">{totalLeavesApproved}</Typography>
                          </Box>
                          <Box
                            onClick={() => navigate("/Leave/approval", { state: { status: 84, month: selectedMonth, year: selectedYear, companyID: activeCompanyFilter, companyName: activeCompanyName } })}
                            sx={{ display: "flex", justifyContent: "space-between", cursor: "pointer", "&:hover": { opacity: 0.8 } }}
                          >
                            <Typography variant="caption" color="text.secondary">Pending</Typography>
                            <Typography variant="caption" fontWeight={800} color="#f59e0b">{totalLeavesPending}</Typography>
                          </Box>
                          <Box
                            onClick={() => navigate("/Leave/approval", { state: { status: 86, month: selectedMonth, year: selectedYear, companyID: activeCompanyFilter, companyName: activeCompanyName } })}
                            sx={{ display: "flex", justifyContent: "space-between", cursor: "pointer", "&:hover": { opacity: 0.8 } }}
                          >
                            <Typography variant="caption" color="text.secondary">Rejected</Typography>
                            <Typography variant="caption" fontWeight={800} color="#06b6d4">{totalLeavesRejected}</Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Card>
              </Grid>

              {/* Card 10: Pending Actions */}
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 3, borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.02)", bgcolor: "#fff", height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={800} color="#0f172a" sx={{ mb: 3 }}>
                      PENDING ACTIONS
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 1, textAlign: "center" }}>
                      {/* Action 1: Leave Approvals */}
                      <Grid item xs={6} sm={4} md={2.4} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <Avatar sx={{ bgcolor: "#e0f2fe", color: "#0369a1", mb: 1, width: 42, height: 42 }}>
                          <CalendarMonthIcon sx={{ fontSize: 22 }} />
                        </Avatar>
                        <Typography variant="caption" fontWeight={800} color="#1e293b" sx={{ fontSize: "9px" }}>Leave Approvals</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "8px" }}>{totalLeavesPending} Pending</Typography>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => navigate("/Leave/approval", { state: { status: 84, companyID: activeCompanyFilter } })}
                          sx={{ textTransform: "none", fontSize: "10px", fontWeight: "bold", mt: 0.5, minWidth: "auto", p: 0 }}
                        >
                          Review
                        </Button>
                      </Grid>

                      {/* Action 2: Attendance Issues */}
                      <Grid item xs={6} sm={4} md={2.4} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <Avatar sx={{ bgcolor: "#fffbeb", color: "#ca8a04", mb: 1, width: 42, height: 42 }}>
                          <AlarmIcon sx={{ fontSize: 22 }} />
                        </Avatar>
                        <Typography variant="caption" fontWeight={800} color="#1e293b" sx={{ fontSize: "9px" }}>Attendance Issues</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "8px" }}>{hrPayrollMetrics.longDaysInactive?.length || 0} Exceptions</Typography>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => navigate("/employee-attendance", { state: { status: "exception", companyID: activeCompanyFilter } })}
                          sx={{ textTransform: "none", fontSize: "10px", fontWeight: "bold", mt: 0.5, minWidth: "auto", p: 0 }}
                        >
                          Review
                        </Button>
                      </Grid>

                      {/* Action 3: Payroll Approval */}
                      <Grid item xs={6} sm={4} md={2.4} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <Avatar sx={{ bgcolor: "#f3e8ff", color: "#7e22ce", mb: 1, width: 42, height: 42 }}>
                          <PaymentsIcon sx={{ fontSize: 22 }} />
                        </Avatar>
                        <Typography variant="caption" fontWeight={800} color="#1e293b" sx={{ fontSize: "9px" }}>Payroll Approval</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "8px" }}>{payrollDraftCount} Pending</Typography>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => navigate("/payroll-process", { state: { month: selectedMonth, year: selectedYear, companyID: activeCompanyFilter } })}
                          sx={{ textTransform: "none", fontSize: "10px", fontWeight: "bold", mt: 0.5, minWidth: "auto", p: 0 }}
                        >
                          Review
                        </Button>
                      </Grid>

                      {/* Action 4: Payslip Generation */}
                      <Grid item xs={6} sm={4} md={2.4} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <Avatar sx={{ bgcolor: "#e0f2fe", color: "#0284c7", mb: 1, width: 42, height: 42 }}>
                          <ReceiptLongIcon sx={{ fontSize: 22 }} />
                        </Avatar>
                        <Typography variant="caption" fontWeight={800} color="#1e293b" sx={{ fontSize: "9px" }}>Payslip Gen</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "8px" }}>{payrollProcessedCount} Pending</Typography>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => navigate("/payslip-report", { state: { month: selectedMonth, year: selectedYear, companyID: activeCompanyFilter } })}
                          sx={{ textTransform: "none", fontSize: "10px", fontWeight: "bold", mt: 0.5, minWidth: "auto", p: 0 }}
                        >
                          Generate
                        </Button>
                      </Grid>

                      {/* Action 5: Employee Profiles */}
                      <Grid item xs={6} sm={4} md={2.4} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <Avatar sx={{ bgcolor: "#fce7f3", color: "#db2777", mb: 1, width: 42, height: 42 }}>
                          <PersonIcon sx={{ fontSize: 22 }} />
                        </Avatar>
                        <Typography variant="caption" fontWeight={800} color="#1e293b" sx={{ fontSize: "9px" }}>Employee Profiles</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "8px" }}>{incompleteProfilesCount} Incomplete</Typography>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => navigate("/employee/list", { state: { incomplete: true, companyID: activeCompanyFilter } })}
                          sx={{ textTransform: "none", fontSize: "10px", fontWeight: "bold", mt: 0.5, minWidth: "auto", p: 0 }}
                        >
                          Review
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </>
        )}

        {/* ROW 5: WORKFORCE DIVERSITY & GEOGRAPHY */}
        {/* <Grid container spacing={3} sx={{ mb: 4, marginTop: "50px" }}>
          <Grid item xs={12}>
            <Card sx={{ p: 3, borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.02)", bgcolor: "#fff" }}>
              <Typography variant="subtitle2" fontWeight={800} color="#0f172a" sx={{ mb: 3, textTransform: "uppercase" }}>
                Workforce Demographics & Diversity
              </Typography>
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={12} md={5} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <Typography variant="caption" fontWeight={800} color="#64748b" display="block" sx={{ mb: 2, textTransform: "uppercase" }}>
                    Gender Diversity
                  </Typography>
                  {genderSeries.length > 0 ? (
                    <ReactApexChart options={genderChartOptions} series={genderSeries} type="donut" height={220} width="100%" />
                  ) : (
                    <Typography variant="caption" color="text.secondary">No gender data available</Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={7}>
                  <Typography variant="caption" fontWeight={800} color="#64748b" display="block" sx={{ mb: 2, textTransform: "uppercase" }}>
                    Top Cities (Geographical Talent Pool)
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.8 }}>
                    {hrPayrollMetrics.geographical && hrPayrollMetrics.geographical.length > 0 ? (
                      hrPayrollMetrics.geographical.map((geo, idx) => (
                        <Box key={idx} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Typography variant="caption" fontWeight={750} color="#475569" sx={{ minWidth: 100 }}>{geo.city}</Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexGrow: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={activeCountVal ? (geo.count / activeCountVal) * 100 : 0}
                              sx={{ flexGrow: 1, height: 8, borderRadius: 4, bgcolor: "#f1f5f9", "& .MuiLinearProgress-bar": { bgcolor: "#6366f1" } }}
                            />
                            <Typography variant="caption" fontWeight={850} color="#0f172a">{geo.count} ({activeCountVal ? Math.round((geo.count / activeCountVal) * 100) : 0}%)</Typography>
                          </Box>
                        </Box>
                      ))
                    ) : (
                      <Typography variant="caption" color="text.secondary">No geographical data available</Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid> */}

        <Zoom in={showScroll}>
          <Fab
            onClick={scrollToTop}
            aria-label="scroll back to top"
            sx={{
              position: "fixed",
              bottom: 24,
              right: 24,
              background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
              color: "#fff",
              boxShadow: "0 6px 20px rgba(99, 102, 241, 0.3)",
              zIndex: 9999,
              border: "1px solid rgba(255, 255, 255, 0.15)",
              backdropFilter: "blur(4px)",
              width: 48,
              height: 48,
              transition: "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
              "&:hover": {
                background: "linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)",
                transform: "translateY(-4px) scale(1.05)",
                boxShadow: "0 10px 25px rgba(99, 102, 241, 0.45)",
              },
            }}
          >
            <ArrowUpwardIcon />
          </Fab>
        </Zoom>
      </Box>
    </Layout>
  );
}

export default Dashboard;
